import type { SVGProps } from 'react'

/**
 * Inline icon set — one 24×24 grid, 1.5px stroke, `currentColor`, no dependency and no icon font.
 *
 * Accessibility: an icon is decorative by default (`aria-hidden`, `focusable="false"`). When the
 * icon *is* the meaning — an icon-only button, a status glyph — pass `label` and it becomes
 * `role="img"` with that accessible name.
 */

export type IconName =
  | 'home'
  | 'menu'
  | 'pen'
  | 'document'
  | 'upload'
  | 'download'
  | 'scan'
  | 'camera'
  | 'printer'
  | 'sparkle'
  | 'id-card'
  | 'send'
  | 'share'
  | 'link'
  | 'copy'
  | 'check'
  | 'clock'
  | 'calendar'
  | 'user'
  | 'template'
  | 'search'
  | 'filter'
  | 'chart'
  | 'settings'
  | 'plug'
  | 'shield'
  | 'lock'
  | 'warning'
  | 'info'
  | 'error'
  | 'close'
  | 'chevron-up'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'plus'
  | 'trash'
  | 'external-link'
  | 'spinner'
  | 'refresh'
  | 'undo'
  | 'redo'
  | 'more'
  | 'eye'
  | 'text'
  | 'checkbox'
  | 'mail'
  | 'building'
  | 'briefcase'
  | 'sigma'
  | 'initials'
  | 'minus'
  | 'expand'
  | 'fit-width'
  | 'rotate'
  | 'clipboard'
  | 'qr'

/** Every icon name, in the order the reference page lists them. */
export const ICON_NAMES: readonly IconName[] = [
  'home',
  'menu',
  'document',
  'upload',
  'download',
  'scan',
  'camera',
  'printer',
  'sparkle',
  'id-card',
  'send',
  'share',
  'link',
  'copy',
  'check',
  'clock',
  'calendar',
  'user',
  'template',
  'search',
  'filter',
  'chart',
  'settings',
  'plug',
  'shield',
  'lock',
  'warning',
  'info',
  'error',
  'close',
  'chevron-up',
  'chevron-down',
  'chevron-left',
  'chevron-right',
  'plus',
  'trash',
  'external-link',
  'spinner',
  'refresh',
  'pen',
  'undo',
  'redo',
  'more',
  'eye',
  'text',
  'checkbox',
  'mail',
  'building',
  'briefcase',
  'sigma',
  'initials',
  'minus',
  'expand',
  'fit-width',
  'rotate',
  'clipboard',
  'qr',
]

/** Path data per icon, drawn in a 24×24 viewBox. */
const PATHS: Record<IconName, readonly string[]> = {
  home: ['M4 11 12 4l8 7', 'M6 9.6V20h12V9.6'],
  menu: ['M4 7h16', 'M4 12h16', 'M4 17h16'],
  pen: ['M15 5l4 4L8 20H4v-4z', 'M13 7l4 4', 'M4 22h16'],
  document: ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z', 'M14 3v5h5'],
  upload: ['M12 16V4', 'M7 9l5-5 5 5', 'M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2'],
  download: ['M12 4v12', 'M7 11l5 5 5-5', 'M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2'],
  scan: [
    'M4 8V6a2 2 0 0 1 2-2h2',
    'M16 4h2a2 2 0 0 1 2 2v2',
    'M20 16v2a2 2 0 0 1-2 2h-2',
    'M8 20H6a2 2 0 0 1-2-2v-2',
    'M4 12h16',
  ],
  camera: [
    'M4 8.5a2 2 0 0 1 2-2h2l1.5-2h5L16 6.5h2a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z',
    'M12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  ],
  printer: [
    'M7 9V4h10v5',
    'M7 17H5a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-2',
    'M7 14h10v6H7z',
  ],
  sparkle: ['M12 3.5 13.8 10l6.7 2-6.7 2L12 20.5 10.2 14l-6.7-2 6.7-2z', 'M19 3.5v3', 'M17.5 5h3'],
  'id-card': [
    'M3.5 6.5h17v11h-17z',
    'M9 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    'M6 15.5a3 3 0 0 1 6 0',
    'M14 10h4',
    'M14 13.5h4',
  ],
  send: ['M21 3 3 10.5l7.5 3 3 7.5z', 'M21 3l-10.5 10.5'],
  share: [
    'M6 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    'M18 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    'M18 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    'm8.7 10.6 6.6-3.2',
    'm8.7 13.4 6.6 3.2',
  ],
  link: [
    'M10 13a5 5 0 0 0 7.1 0l2.8-2.8a5 5 0 0 0-7.1-7.1L11.4 4.5',
    'M14 11a5 5 0 0 0-7.1 0L4.1 13.8a5 5 0 0 0 7.1 7.1l1.4-1.4',
  ],
  copy: [
    'M8 8h11a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z',
    'M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2',
  ],
  check: ['m4.5 12.5 5 5 10-11'],
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7.5V12l3.2 2'],
  calendar: [
    'M7 3v3',
    'M17 3v3',
    'M4 8.5h16',
    'M5.5 5.5h13A1.5 1.5 0 0 1 20 7v12a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19V7a1.5 1.5 0 0 1 1.5-1.5z',
  ],
  user: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4.5 20.5a7.5 7.5 0 0 1 15 0'],
  template: ['M4 4.5h16v5H4z', 'M4 12.5h7v7H4z', 'M13 12.5h7v7h-7z'],
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z', 'm20 20-3.7-3.7'],
  filter: ['M4 5h16l-6.2 7.2V19l-3.6 2v-8.8z'],
  chart: ['M4 20h16', 'M7.5 20V10', 'M12 20V4.5', 'M16.5 20v-6.5'],
  settings: [
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    'M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.6 15H4.5a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.2-2.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 11.5 4.6V4.5a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0 1.2 2.9h.1a2 2 0 1 1 0 4h-.1z',
  ],
  plug: ['M9 3v5', 'M15 3v5', 'M6 8h12v3a6 6 0 0 1-12 0z', 'M12 17v4'],
  shield: ['M12 3l7.5 2.8v5.7c0 4.3-3 7.9-7.5 9-4.5-1.1-7.5-4.7-7.5-9V5.8z'],
  lock: [
    'M7.5 11V8a4.5 4.5 0 0 1 9 0v3',
    'M6.5 11h11a1 1 0 0 1 1 1v7.5a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1V12a1 1 0 0 1 1-1z',
  ],
  warning: ['M12 4.5 21 20H3z', 'M12 10v4', 'M12 17.2h.01'],
  info: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 11v5.5', 'M12 7.8h.01'],
  error: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'm9.2 9.2 5.6 5.6', 'm14.8 9.2-5.6 5.6'],
  close: ['m6 6 12 12', 'm18 6-12 12'],
  'chevron-up': ['m6 15 6-6 6 6'],
  'chevron-down': ['m6 9 6 6 6-6'],
  'chevron-left': ['m14 6-6 6 6 6'],
  'chevron-right': ['m10 6 6 6-6 6'],
  plus: ['M12 5v14', 'M5 12h14'],
  trash: ['M4 7h16', 'M9.5 7V5h5v2', 'M6.5 7l.9 13h9.2l.9-13', 'M10.5 11v5', 'M13.5 11v5'],
  'external-link': [
    'M14 4h6v6',
    'M20 4l-8.5 8.5',
    'M18 14.5V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4.5',
  ],
  spinner: ['M12 3a9 9 0 1 0 9 9'],
  refresh: [
    'M4.5 12a7.5 7.5 0 0 1 12.8-5.3L20 9.4',
    'M20 4.5v4.9h-4.9',
    'M19.5 12a7.5 7.5 0 0 1-12.8 5.3L4 14.6',
    'M4 19.5v-4.9h4.9',
  ],
  undo: ['M9 14 4 9l5-5', 'M4 9h10.5a5.5 5.5 0 0 1 0 11H11'],
  redo: ['m15 14 5-5-5-5', 'M20 9H9.5a5.5 5.5 0 0 0 0 11H13'],
  more: ['M6 12h.01', 'M12 12h.01', 'M18 12h.01'],
  eye: [
    'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z',
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  ],
  text: ['M5 6V4.5h14V6', 'M12 4.5v15', 'M9 19.5h6'],
  checkbox: [
    'M5.5 4.5h13a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1z',
    'm8 12 3 3 5-6',
  ],
  mail: [
    'M4.5 5.5h15a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1z',
    'm4 7 8 6 8-6',
  ],
  building: [
    'M5 20.5V4.5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16',
    'M15 9.5h3a1 1 0 0 1 1 1v10',
    'M3.5 20.5h17',
    'M8.5 7.5h3',
    'M8.5 11h3',
    'M8.5 14.5h3',
  ],
  briefcase: [
    'M4.5 7.5h15a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1v-10a1 1 0 0 1 1-1z',
    'M9 7.5v-2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2',
    'M3.5 12.5h17',
  ],
  sigma: ['M17.5 5.5V4h-11l6 8-6 8h11v-1.5'],
  initials: ['M3.5 17 7 7l3.5 10', 'M4.7 13.5h4.6', 'M14 7h6l-6 10h6'],
  minus: ['M5 12h14'],
  expand: ['M4 9V4h5', 'M20 9V4h-5', 'M4 15v5h5', 'M20 15v5h-5'],
  // Two edges and the width between them, arrows to both: the page fills the width.
  'fit-width': ['M3 5v14', 'M21 5v14', 'M7 12h10', 'M10 9l-3 3 3 3', 'M14 9l3 3-3 3'],
  rotate: ['M20 12a8 8 0 1 1-2.3-5.7L20 8.5', 'M20 3.5v5h-5'],
  clipboard: [
    'M9 4.5h6v3H9z',
    'M15 5.5h2.5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1H9',
  ],
  // Three finder squares and a scatter of modules: reads as a QR code, unlike the scan frame.
  qr: [
    'M4 4h6v6H4z',
    'M14 4h6v6h-6z',
    'M4 14h6v6H4z',
    'M14 14h2v2h-2z',
    'M18 18h2v2h-2z',
    'M14 19v1',
    'M19 14h1',
  ],
}

export type IconSize = 20 | 24

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name' | 'width' | 'height'> {
  readonly name: IconName
  readonly size?: IconSize
  /** Accessible name. Omit for a decorative icon (`aria-hidden`). */
  readonly label?: string
  readonly className?: string
  /** Grid stroke width; 1.5 at 24px is the house weight. */
  readonly strokeWidth?: number
}

export function Icon({ name, size = 20, label, className, strokeWidth = 1.5, ...rest }: IconProps) {
  const classes = ['op-icon', name === 'spinner' ? 'op-icon--spin' : '', className]
    .filter(Boolean)
    .join(' ')
  const semantics =
    label === undefined
      ? ({ 'aria-hidden': true, focusable: 'false' } as const)
      : ({ role: 'img', 'aria-label': label } as const)
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={classes}
      {...semantics}
      {...rest}
    >
      {PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}
